import React, { useEffect, useState } from 'react'
import { IBriefStep, TBriefStepsVariants } from '../types'
import { BriefStepSection } from './BriefStepSection'
import { BriefColorSelection } from './entryFields/BriefColorSelection/BriefColorSelection'
import { BriefFontSelection } from './entryFields/BriefFontSelection/BriefFontSelection'
import { BriefTextField } from './entryFields/BriefTextField'
import { BriefButtonTileSelection } from './entryFields/BriefButtonTileSelection'
import { BriefCardsSelection } from './entryFields/BriefCardsSelection/BriefCardsSelection'
import {
  useProductOption,
  useBriefingFormOutput,
  usePreloadedAnswers,
  useReadOnlyMode,
} from './BriefingFormDataProvider'
import {
  TDesignerControlsDisplayTypes,
  trackDesignerInteraction,
} from '@utils/gtm'
import { useRouter } from 'next/router'
import isEmpty from 'lodash/isEmpty'
import {
  colorConfigPerProduct,
  IBriefColorConfig,
  standartLogoPicker,
} from '../briefingFormProductColors.config'
import { briefStepLabelToKey } from '../utils'

interface IBriefingForm {
  productSlug: string
  contentConfig: IBriefStep[]
}

export function BriefingForm({ productSlug, contentConfig }: IBriefingForm) {
  const { briefingFormOutput, setBriefingFormOutput } = useBriefingFormOutput()
  const router = useRouter()
  const productOption = useProductOption()
  const [productColorConfig, setProductColorConfig] =
    useState<IBriefColorConfig>(
      colorConfigPerProduct[productSlug]?.default || standartLogoPicker
    )
  const preloadedAnswers = usePreloadedAnswers()
  const readOnlyMode = useReadOnlyMode()

  const onSelectionChange = ({
    selectedValues,
    briefStep,
  }: {
    selectedValues: Record<string, string | string[]>
    briefStep: IBriefStep
  }) => {
    setBriefingFormOutput({
      ...briefingFormOutput,
      ...selectedValues,
    })

    const selectionIsValid =
      Object.values(selectedValues) &&
      !isEmpty(Object.values(selectedValues)?.[0])

    if (selectionIsValid) {
      const valueToSubmit = Object.values(selectedValues)[0]

      trackDesignerInteraction({
        event: 'designer-interaction',
        designerName: 'briefing',
        componentName: briefStep.label,
        componentInformation:
          briefStep.variant === 'font-selector'
            ? valueToSubmit.slice(-1)?.[0]
            : valueToSubmit,
        selectedProductSlug: router.query?.slug.toString(),
        displayType: briefStep.variant
          ?.split('-')
          .map((word) => word[0]?.toUpperCase() + word?.slice(1))
          .join('') as TDesignerControlsDisplayTypes,
      })
    }
  }

  const optionsToEntryComponentMapper: Record<
    TBriefStepsVariants,
    (props: any) => JSX.Element
  > = {
    'color-selector': BriefColorSelection,
    'textual-answer': BriefTextField,
    'cards-selector-slider': BriefCardsSelection,
    'font-selector': BriefFontSelection,
    'button-tiles-selector': BriefButtonTileSelection,
  }

  useEffect(() => {
    let colorConfig

    if (productOption.colors || productOption.color) {
      colorConfig =
        colorConfigPerProduct[productSlug]?.[
          productOption.colors || productOption.color
        ]
    }

    if (isEmpty(colorConfig)) {
      colorConfig =
        colorConfigPerProduct[productSlug]?.default || standartLogoPicker
    }

    setProductColorConfig(colorConfig)
  }, [productOption.colors, productOption.color])

  return (
    <div className="flex flex-col w-full mb-12">
      {contentConfig?.map((briefStep) => {
        const MappedComponent = optionsToEntryComponentMapper[briefStep.variant]

        return (
          <BriefStepSection
            label={briefStep.label}
            tooltip={briefStep.tooltip}
            key={briefStep.key || briefStep.label}
            description={briefStep.description}
          >
            {briefStep.variant === 'color-selector' && (
              <BriefColorSelection
                initialPickersConfig={productColorConfig.pickers}
                mode={productColorConfig?.mode}
                cap={productColorConfig?.cap}
                pickerTemplate={productColorConfig?.pickerTemplate || null}
                onChange={(selectedValues) =>
                  onSelectionChange({ selectedValues, briefStep })
                }
              />
            )}

            {briefStep.variant !== 'color-selector' && briefStep.options && (
              <MappedComponent
                options={briefStep.options}
                valueKey={
                  briefStep.key || briefStepLabelToKey(briefStep?.label)
                }
                onChange={(selectedValues) =>
                  onSelectionChange({ selectedValues, briefStep })
                }
              />
            )}
          </BriefStepSection>
        )
      })}
    </div>
  )
}
